import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import Icon from "../AppIcon";

/**
 * Minimal toast system (UX audit fix): success/error feedback for actions
 * that previously had none. Usage:
 *   const toast = useToast();
 *   toast.success("Lead saved.");
 *   toast.error("Could not delete.");
 * Auto-dismisses after 3.5s; click X to dismiss; stack up to 5.
 */

const ToastContext = createContext(null);

let idCounter = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (type, message) => {
      const id = ++idCounter;
      setToasts((prev) => [...prev.slice(-4), { id, type, message }]);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), 3500),
      );
    },
    [dismiss],
  );

  const api = useMemo(
    () => ({
      success: (message) => push("success", message),
      error: (message) => push("error", message),
      info: (message) => push("info", message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 w-[calc(100vw-2rem)] max-w-sm pointer-events-none"
        role="status"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg animate-toast-in"
            style={{
              borderColor:
                toast.type === "success"
                  ? "var(--color-success)"
                  : toast.type === "error"
                    ? "var(--color-error)"
                    : "var(--color-border)",
              background: "var(--color-card)",
            }}
          >
            <Icon
              name={toast.type === "success" ? "CheckCircle" : toast.type === "error" ? "AlertTriangle" : "Info"}
              size={16}
              color={
                toast.type === "success"
                  ? "var(--color-success)"
                  : toast.type === "error"
                    ? "var(--color-error)"
                    : "var(--color-primary)"
              }
              className="mt-0.5 flex-shrink-0"
            />
            <p className="font-caption text-sm flex-1" style={{ color: "var(--color-foreground)" }}>
              {toast.message}
            </p>
            <button
              onClick={() => dismiss(toast.id)}
              className="flex-shrink-0 p-0.5 rounded hover:opacity-70"
              aria-label="Dismiss notification"
            >
              <Icon name="X" size={14} color="var(--color-text-secondary)" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider.");
  }
  return ctx;
}